// Recording what the player did in a room, so it can be handed to somebody else.
//
// The format is deliberately **not new**: it is `sim/replay.ts`'s `Recording`,
// the same artifact the World Editor's takes and the library's `intended` takes
// already use, and the same one `npm test` replays. That buys three things a
// bespoke log would not. It embeds the room, so a file is self-contained and
// cannot be invalidated by the world being re-authored afterwards. It carries the
// `PhysicsConfig` it was played on, so a take made on tuned dials replays on
// those dials rather than on today's defaults. And it is *executable* -- whoever
// receives it runs `replay()` on it and gets the world back, rather than reading
// a description of it.
//
// **The take verifies itself before it is handed over.** `downloadTake` replays
// the recording it is about to write and compares the result against the world
// the player is actually standing in (`worldDigest`). A faithful take reports
// `replayMatches: true`; one that diverges says so, in the file and in the UI,
// and lists what came out different. That matters because this module is a
// *reconstruction*: the game does not run its casts through `applyCommand` (see
// `turnActions.ts` for why -- routing them through the sim's `cast()` would
// throw away the narration, which is most of what the game is), so every command
// here is captured at the site that performs the equivalent action rather than
// being the thing that performed it. A reconstruction that quietly drifted would
// be worse than no recording at all, so it checks.
//
// What is captured, and where:
//
//   tick       every frame's elapsed ms, accumulated (see `recordElapsed`)
//   step/wait  `GameScene`, at the two `applyCommand` calls it already makes
//   cast       `ui.ts`, from the same request it hands `castAction`
//   mark       `objectLink.ts`, both setting and clearing
//   interact   `ui.ts`, from the id it hands `applyInteract`
//
// **One known gap**: using a carried key on a locked doorway writes `doorLocks`
// directly (`ui.ts`'s door panel) rather than going through the sim's
// `applyUnlock`, so it is not recorded. Door locks are not part of `worldDigest`
// either, so this does not show up as a divergence -- it is written down here
// because "the check passed" must not be read as "everything was captured".
import { Recording, RECORDING_FORMAT_VERSION, SimCommand, replay, stringifyRecording, worldDigest } from '../../rune-lab/src/sim/replay';
import { WorldState } from '../../rune-lab/src/sim/state';
import { RoomDef } from '../../rune-lab/src/sim/types';
import { getWorld } from './activeWorld';

interface Take {
  roomId: string;
  room: RoomDef;
  start: Recording['start'];
  commands: SimCommand[];
  /** Elapsed milliseconds not yet written into a `tick` command. Flushed
   * immediately before the next real command, and again when the take is
   * exported, so a recording carries the same total elapsed time the world
   * actually saw. Coalescing is exact rather than approximate: `tick` carries
   * its own leftover remainder between calls (see `sim/tick.ts`), so one call of
   * 1600 ms and a hundred calls of 16 ms fire the same rules the same number of
   * times. */
  pendingMs: number;
}

let take: Take | null = null;

/** Starts a fresh take for the room the player has just entered.
 *
 * Called from `enterRoom`, beside `clearUndoHistory`, and for the same reason
 * that call is there: arriving is a break in continuity. A `Recording` covers one
 * room by construction (every command in it is room-local), so a take that
 * spanned a doorway would replay against a world that never existed.
 *
 * The room is deep-cloned at this instant rather than read from the shipped
 * files, so a take started on a second visit begins from the room as the player
 * left it -- which is the state the commands that follow actually ran against. */
export function beginTake(world: WorldState, roomId: string): void {
  const room = world.rooms[roomId];
  if (!room) {
    take = null;
    return;
  }
  take = {
    roomId,
    room: structuredClone(room),
    start: {
      x: world.player.x,
      y: world.player.y,
      facing: world.player.facing,
      mana: world.player.mana,
      health: world.player.health,
      charge: world.player.charge,
      // The caster's warmth and the two ambient remainders: without them a take
      // begun after the world had been running replays from a fresh world's
      // defaults, which diverges on `thermalState` and shifts every decay tick.
      // Found by this module's own self-check on the very first room-jump take.
      thermalState: world.player.thermalState,
      sinceManaRegenMs: world.sinceManaRegenMs,
      sinceRoomDecayMs: world.sinceRoomDecayMs,
      markedSourceId: world.markedSource?.id ?? null,
    },
    commands: [],
    pendingMs: 0,
  };
}

/** Time passing. Accumulated rather than written per frame: sixty `tick`
 * commands a second would make a minute of standing still a 3,600-entry file
 * describing nothing. */
export function recordElapsed(ms: number): void {
  if (take && ms > 0) take.pendingMs += ms;
}

function flushElapsed(): void {
  if (!take || take.pendingMs <= 0) return;
  take.commands.push({ kind: 'tick', ms: take.pendingMs });
  take.pendingMs = 0;
}

/** One thing the player did. Any pending elapsed time is written first, so the
 * order in the file is the order it happened in. */
export function recordCommand(command: SimCommand): void {
  if (!take) return;
  flushElapsed();
  take.commands.push(command);
}

/** How many real commands the current take holds -- what the Developer Tools
 * button shows, so it is obvious whether there is anything worth downloading.
 * Excludes the pending tick, which is time rather than a move. */
export function takeCommandCount(): number {
  return take ? take.commands.filter((c) => c.kind !== 'tick').length : 0;
}

export function takeRoomId(): string | null {
  return take?.roomId ?? null;
}

/** The take as a `Recording`, ending on the world the player is standing in.
 *
 * `expected` is the digest of the *live* world rather than of the replayed one,
 * which is what makes the self-check below meaningful: replaying the file has to
 * arrive where the game actually is, not where the file says it should.
 */
export function currentTake(world: WorldState): Recording | null {
  if (!take) return null;
  flushElapsed();
  return {
    formatVersion: RECORDING_FORMAT_VERSION,
    room: structuredClone(take.room),
    physics: structuredClone(world.physics),
    start: { ...take.start },
    commands: structuredClone(take.commands),
    expected: worldDigest(world),
  };
}

export interface TakeCheck {
  recording: Recording;
  /** Whether replaying the file lands on the world the game is actually in. */
  matches: boolean;
  /** What came out different, empty when it matches. */
  differences: string[];
}

/** Builds the take and replays it, so the file says whether it is faithful.
 *
 * Runs on a world of the recording's own making (`replay` builds one from the
 * embedded room), so this cannot disturb the game the player is in the middle
 * of. */
export function checkTake(world: WorldState): TakeCheck | null {
  const recording = currentTake(world);
  if (!recording) return null;
  try {
    const result = replay(recording);
    return { recording, matches: result.matches === true, differences: result.differences };
  } catch (err) {
    return { recording, matches: false, differences: [`replay threw: ${err instanceof Error ? err.message : String(err)}`] };
  }
}

/** The filename a take is offered under: the room and the number of moves, which
 * between them are what anybody naming one by hand would have written. */
export function takeFileName(recording: Recording): string {
  const moves = recording.commands.filter((c) => c.kind !== 'tick').length;
  return `take-${recording.room.id}-${moves}moves.json`;
}

/** Writes the current take out as a file.
 *
 * Returns what the check found so the caller can say so, or null when there is
 * no take at all. A `Blob` and a synthetic `<a download>` rather than anything
 * cleverer: this is the game's own page, not a sandboxed frame, and the file has
 * to arrive somewhere the player can attach it to a message.
 */
export function downloadTake(): TakeCheck | null {
  const check = checkTake(getWorld());
  if (!check) return null;
  // The self-check result travels *in* the file as well as being reported here.
  // A take that is handed on hours later should not need the sender to remember
  // whether the dialog said it verified.
  const body = stringifyRecording({
    ...check.recording,
    ...({ replayMatches: check.matches, replayDifferences: check.differences } as Partial<Recording>),
  });
  const url = URL.createObjectURL(new Blob([body], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = takeFileName(check.recording);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next frame rather than immediately: Safari has been known to
  // cancel a download whose object URL is released in the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return check;
}
